import api from './api';

export interface AppSetting {
  setting_key: string;
  setting_value: string;
  description: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

/** One column's slice of the Change All zero-date repair plan (preview), plus
 * `written`/`raced` after an apply. */
export interface ChangeAllRepairColumnResult {
  column: string;
  candidates: number;
  toWrite: number;
  skipped: Record<string, number>;
  sample: { pronumber: string; value: string }[];
  written?: number;
  raced?: string[];
}

/** One table's slice of the permission sync plan (preview), plus `applied`
 * after an apply. */
export interface PermissionSyncTableResult {
  table: string;
  inserts: number;
  updates: number;
  deletes: number;
  sample: Record<string, unknown>[];
  applied?: boolean;
}

export interface PermissionSyncResult {
  tables: PermissionSyncTableResult[];
  total_changes: number;
}

class AppSettingsService {
  /**
   * Get all app settings
   */
  async getAll(): Promise<AppSetting[]> {
    const response = await api.get('/app-settings/list');
    return response.data.data;
  }

  /**
   * Update a single app setting
   */
  async update(settingKey: string, settingValue: string): Promise<void> {
    await api.put(`/app-settings/${settingKey}`, {
      setting_value: settingValue
    });
  }

  /**
   * Plan the Change All zero-date repair (read-only) — what apply would write.
   */
  async previewChangeAllRepair(): Promise<ChangeAllRepairColumnResult[]> {
    const response = await api.post('/app-settings/change-all-repair/preview', {});
    return response.data.columns;
  }

  /**
   * Run the Change All zero-date repair. The server recomputes the plan —
   * the preview is informational, so a save landing in between is never clobbered.
   */
  async applyChangeAllRepair(): Promise<ChangeAllRepairColumnResult[]> {
    const response = await api.post('/app-settings/change-all-repair/apply', {});
    return response.data.columns;
  }

  /**
   * Plan the prod→test permission sync (read-only) — what apply would change.
   */
  async previewPermissionSync(): Promise<PermissionSyncResult> {
    const response = await api.post('/app-settings/permission-sync/preview', {});
    return response.data;
  }

  /**
   * Run the permission sync. The server recomputes the plan and snapshots
   * every table it touches before writing — the preview is informational.
   */
  async applyPermissionSync(): Promise<PermissionSyncResult> {
    const response = await api.post('/app-settings/permission-sync/apply', {});
    return response.data;
  }
}

export const appSettingsService = new AppSettingsService();
export default appSettingsService;
